import { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { api } from "@/lib/api";
import { PageHeader, SectionTitle, Spinner } from "@/components/kit";
import { EmotionTrends, BestSkills } from "@/components/insights/EmotionInsights";
import { EmotionWeekdays } from "@/components/insights/EmotionWeekdays";
import { MonthlyStory } from "@/components/insights/MonthlyStory";
import { NudgeResults } from "@/components/insights/NudgeResults";
import { YearInReview } from "@/components/insights/YearInReview";
import { WellbeingInsights } from "@/components/insights/WellbeingInsights";
import { SleepCompare } from "@/components/bedtime/SleepCompare";
import { Link } from "react-router-dom";

const TICK = { fontSize: 11, fill: "var(--mm-text-secondary)" };
const SMALL_TICK = { fontSize: 11 };
const MOOD_DOMAIN = [0, 10];

const Stat = ({ label, value, testId }) => (
  <div className="mm-card p-4" data-testid={testId}><div className="text-xs text-mm-muted">{label}</div><div className="font-display text-2xl font-bold mt-1">{value}</div></div>
);

const TopList = ({ title, items }) => (
  <div className="mm-card p-5">
    <h3 className="font-semibold mb-3">{title}</h3>
    {items?.length ? items.map((i) => (
      <div key={i.name || i[0]} className="flex justify-between text-sm py-1.5"><span>{i.name || i[0]}</span><span className="text-mm-muted">{i.count ?? i[1]}×</span></div>
    )) : <p className="text-sm text-mm-muted">Not enough data yet</p>}
  </div>
);

const CHART_SIZE = { width: 320, height: 224 };
const BAR_RADIUS = [8, 8, 0, 0];

export default function Insights() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);
  const [skills, setSkills] = useState(null);

  useEffect(() => { api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => setStats({})); }, []);
  useEffect(() => {
    Promise.all(["mood", "energy", "anxiety"].map((m) => api.get(`/stats/chart-data?days=${days}&metric=${m}`).then((r) => r.data).catch(() => [])))
      .then(([mo, en, an]) => setChart(mo.filter((d, i) => d.value != null || en[i]?.value != null).map((d, i) => ({ date: d.date.slice(5), mood: d.value, energy: en[i]?.value, anxiety: an[i]?.value }))));
    api.get(`/skill-logs/insights?days=${days}`).then((r) => setSkills(r.data)).catch(() => {});
  }, [days]);

  if (!stats) return <Spinner />;
  const f = (v) => (v ? Number(v).toFixed(1) : "—");
  return (
    <div data-testid="insights-page">
      <PageHeader back title="Insights" subtitle="Patterns from your check-ins" right={
        <div className="flex gap-1">{[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} className={`mm-chip !py-1.5 ${days === d ? "mm-chip-on" : ""}`} data-testid={`insights-range-${d}`}>{d}d</button>)}</div>
      } />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mm-stagger">
        <Stat testId="insights-avg-mood" label="Avg mood" value={f(stats.avg_mood)} />
        <Stat label="Avg energy" value={f(stats.avg_energy)} />
        <Stat label="Avg anxiety" value={f(stats.avg_anxiety)} />
        <Stat label="Avg sleep" value={stats.avg_sleep ? `${f(stats.avg_sleep)}h` : "—"} />
        <Stat label="Total check-ins" value={stats.total_checkins ?? 0} />
        <Stat label="Current streak" value={stats.current_streak ?? 0} />
        <Stat label="Longest streak" value={stats.longest_streak ?? 0} />
        <Stat label="Mood trend" value={stats.mood_trend || "stable"} />
      </div>

      <SectionTitle>Mood & energy over time</SectionTitle>
      <div className="mm-card p-5 h-72" data-testid="insights-mood-chart">
        {chart.length ? (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={CHART_SIZE}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mm-border)" />
              <XAxis dataKey="date" tick={TICK} />
              <YAxis domain={MOOD_DOMAIN} tick={TICK} />
              <Tooltip />
              <Line type="monotone" dataKey="mood" stroke="var(--mm-primary)" strokeWidth={3} dot={false} />
              <Line type="monotone" dataKey="energy" stroke="var(--mm-secondary)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="anxiety" stroke="var(--mm-tertiary)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="h-full flex items-center justify-center text-mm-muted text-sm">Check in a few times to see your chart</div>}
      </div>

      <div className="mt-4"><WellbeingInsights /></div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <SleepCompare />
        <Link to="/body-map?tab=patterns" className="mm-card mm-card-hover p-5 flex items-center gap-4" data-testid="insights-body-map-link">
          <div className="text-3xl" aria-hidden>🫶</div>
          <div><div className="font-semibold">Where stress shows up in your body</div><p className="text-sm text-mm-muted">See your 30-day body map and patterns</p></div>
        </Link>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <TopList title="Top activities" items={stats.top_activities} />
        <TopList title="Top triggers" items={stats.top_triggers} />
        <TopList title="Top mood tags" items={stats.top_mood_tags} />
      </div>

      <SectionTitle>Emotions & what helps</SectionTitle>
      <div className="grid lg:grid-cols-2 gap-4">
        <EmotionTrends />
        <BestSkills skills={skills} />
      </div>
      <div className="mt-4"><EmotionWeekdays /></div>
      <div className="mt-4"><MonthlyStory /></div>
      <div className="mt-4"><YearInReview /></div>
      <div className="mt-4"><NudgeResults /></div>

      <SectionTitle>Coping skill effectiveness</SectionTitle>
      <div className="mm-card p-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat label="Skill uses" value={skills?.total_uses ?? 0} />
          <Stat label="Stress reduced" value={skills?.total_stress_reduced ?? 0} />
          <Stat label="Avg reduction" value={f(skills?.avg_stress_reduction)} />
        </div>
        {skills?.most_effective_skills?.length ? (
          <div className="h-56"><ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={CHART_SIZE}><BarChart data={skills.most_effective_skills}><XAxis dataKey="skill_name" tick={SMALL_TICK} /><YAxis /><Tooltip /><Bar dataKey="avg_stress_reduction" fill="var(--mm-secondary)" radius={BAR_RADIUS} /></BarChart></ResponsiveContainer></div>
        ) : <p className="text-sm text-mm-muted">Log a coping skill from the Coping library to see what works for you.</p>}
      </div>
    </div>
  );
}
